import React, { Dispatch, SetStateAction, useState } from 'react';
import CQTabs from '../cq-tabs';
import CQTabsPanel from '../cq-tabs/pane';
import CQSubmodules from 'components/cq-submodules';
import { Tooltip } from '@salesforce/design-system-react';
import { useTranslation } from 'react-i18next';
import DefaultTaskEngine from 'tasks/task-engine';
import { IPastRun } from 'interfaces/IHistory.interface';
import { IApplicableModules } from 'interfaces/IUpgradeSchema.interface';
import { IOrgInfo } from 'interfaces/IModal.interface';
import { CQAppConstant } from 'app-constants';
import { getModuleIdentifierForTabIndex, getModuleTabName, isFreshModule, isRefreshModule } from 'common/module.utils';
import { formatReleaseDate } from 'common/date.utils';
import { Statuses } from 'interfaces/ITask.interface';

const TabsPanel: any = CQTabsPanel;

export interface CQModuleProperties {
    setViewHistoryButton: Dispatch<SetStateAction<boolean>>;
    setCurrentModule : Dispatch<SetStateAction<string>>;   
    setLastRunCompletedModule: Dispatch<SetStateAction<string>>;
    notifyRunStart: (runId: string) => void;
    onSelectFreshPackages?: () => void;

    applicableModules:{
        allModules: IApplicableModules[],
        lastFailedModule: IApplicableModules,
        lastRun:IPastRun;
        currentlyActiveModule: IApplicableModules | null;
        lastSkippedModule: IApplicableModules,
        daVersion?: string;
        daReleaseDate?: string;
    }
    orgInfo: IOrgInfo;
}


function CQModule(props : CQModuleProperties) {
    const [discardLastIncompleteRun, setDiscardLastIncompleteRun] = useState<boolean>(false);
    
    const {t, i18n} = useTranslation();

    /**
     * Checks whether a previous module execution has failed.
     *
     * @returns {boolean} True if `lastFailedModule` exists and contains keys; false otherwise.
    */
    function failedPreviously() {
        let failed = props.applicableModules.lastFailedModule != null &&
                    Object.keys(props.applicableModules.lastFailedModule).length > 0;
        console.log('Previously failed: ', failed);
        return failed;
    }

    /**
     * Returns a React component panel displaying the most recent incomplete run.
     *
     * @returns {JSX.Element} A `<TabsPanel>` containing the failed module component.
   */
    function getFailurePanel() {
        const failedTask =  props.applicableModules.lastRun?.tasks?.find(t => t.status === Statuses.Failed)
        return <TabsPanel label="Last Incomplete Run">
            <CQSubmodules module={props.applicableModules.lastFailedModule} failedModule={true} lastRun={true} setViewHistoryButton={props.setViewHistoryButton} orgInfo={props.orgInfo} discardLastIncompleteRun={discardLastIncompleteRun} onDiscardLastIncompleteRun={setDiscardLastIncompleteRun} setCurrentModule={props.setCurrentModule} failedTask={failedTask} setLastRunCompletedModule={props.setLastRunCompletedModule} notifyRunStart={props.notifyRunStart} onSelectFreshPackages={props.onSelectFreshPackages}></CQSubmodules>
        </TabsPanel>
    }

    /**
     * Returns a React component panel displaying the module currently running.
     *
     * @returns {JSX.Element} A `<TabsPanel>` with the active module.
     */
    function currentlyRunningTaskPanel() {
        return <TabsPanel label='Currently Running Module'>
            <CQSubmodules module={props.applicableModules.currentlyActiveModule!} activeTask={true} setViewHistoryButton={props.setViewHistoryButton} orgInfo={props.orgInfo} discardLastIncompleteRun={discardLastIncompleteRun} onDiscardLastIncompleteRun={setDiscardLastIncompleteRun} setCurrentModule={props.setCurrentModule} setLastRunCompletedModule={props.setLastRunCompletedModule} notifyRunStart={props.notifyRunStart} onSelectFreshPackages={props.onSelectFreshPackages}></CQSubmodules>
        </TabsPanel>
    }

    /**
     * Checks whether there is currently an active module being executed.
     *
     * @returns {boolean} True if a non-empty `currentlyActiveModule` exists.
     */
    function currentlyRunningTask(){
        let runningTask = props.applicableModules.currentlyActiveModule != null &&
                    Object.keys(props.applicableModules.currentlyActiveModule).length > 0;
        return runningTask;
    }
    
    /**
     * Determines which panel should be displayed based on current state:
     * - the currently running module
     * - the previously failed module (unless discarded)
     * - or all available modules
     *
     * Also sets `DefaultTaskEngine.isUpdate` depending on whether the last run
     * involved an upgrade module.
     *
     * @returns {JSX.Element | JSX.Element[]} A React component or list of panels.
     */
    function getPanel() {
        if(props.applicableModules?.lastRun && !discardLastIncompleteRun) {
            DefaultTaskEngine.currentModule = props.applicableModules.lastRun.forModule || '';
            props.setCurrentModule(DefaultTaskEngine.currentModule);
        }
        if(currentlyRunningTask() && !discardLastIncompleteRun){
            return currentlyRunningTaskPanel();
        }

        if(failedPreviously() && !discardLastIncompleteRun) {
            return getFailurePanel();
        }
        
        const modules = props.applicableModules.allModules.map(module => {
            // Skip refresh module if no applicable refreshes
            if (isRefreshModule(module.moduleIdentifier)) {
                if (module.applicableRefreshes?.length === 0) {
                    return null;
                }
            }
            // Fresh installation is only ever surfaced via the welcome mat (see
            // CQMainScreen), never as a regular tab here. Rendering it as a tab (even
            // conditionally) shifts subsequent tab indices, which previously caused
            // getModuleIdentifierForTabIndex()'s fixed index mapping to mislabel the
            // Refresh tab as Fresh whenever the Refresh tab itself was skipped above.
            if (isFreshModule(module.moduleIdentifier)) {
                return null;
            }
            return (
                <TabsPanel key={module.moduleIdentifier || CQAppConstant.MODULE_TYPES.UPGRADES} label={
                    <span>
                        {getModuleTabName(module.moduleIdentifier!)}
                    </span>
                }>
                    <CQSubmodules module={module} lastSkippedModule={props.applicableModules?.lastSkippedModule} setViewHistoryButton={props.setViewHistoryButton} orgInfo={props.orgInfo} discardLastIncompleteRun={discardLastIncompleteRun} onDiscardLastIncompleteRun={setDiscardLastIncompleteRun} setCurrentModule={props.setCurrentModule} setLastRunCompletedModule={props.setLastRunCompletedModule} notifyRunStart={props.notifyRunStart} onSelectFreshPackages={props.onSelectFreshPackages}></CQSubmodules>
                </TabsPanel>
            );
        });
        return modules;
    }

    /**
     * Handles the change of the active tab in the component.
     *
     * @param tabIndex - The index of the newly selected tab.
     *
     * This function checks if there are any failed or skipped modules with applicable patches.
     * If such a case exists, it sets the `isUpdatesTab` property to `true`.
     * Otherwise, it sets `isUpdatesTab` to `true` if the selected tab index is `0`,
     * and to `false` for any other tab index.
     */
    function onActiveTabChange(tabIndex:number){        
        const moduleIdentifier = getModuleIdentifierForTabIndex(tabIndex);
        if (moduleIdentifier) {
            props.setCurrentModule(moduleIdentifier);
            DefaultTaskEngine.currentModule = moduleIdentifier;
        }
    }

    /**
     * Footer shown once beneath the vertical tabs nav, common to every panel.
     */
    function getFooter() {
        return (
            <div className="slds-border_top slds-p-around_x-small slds-p-top_large slds-text-align_center slds-text-body_small slds-text-color_weak">
                <p>
                    {t("homepage_pageheader")}
                    <Tooltip
                        id="cq-tabs-about-tooltip"
                        align="top left"
                        triggerClassName="slds-text-align_left slds-m-left_xx-small"
                        assistiveText={{ triggerLearnMoreIcon: t("da_info_icon_label") }}
                        content={
                            <div>
                                <p className="slds-m-bottom_xx-small">{t("homepage_pageheader")}</p>
                                <p className="slds-m-bottom_xx-small">{t("da_info_version_label")} {props.applicableModules.daVersion || CQAppConstant.NOT_AVAILABLE}</p>
                                <p className="slds-m-bottom_xx-small">{t("da_info_release_date_label")} {formatReleaseDate(props.applicableModules.daReleaseDate, i18n.language)}</p>
                                <p>{CQAppConstant.DA_INFO_COPYRIGHT}</p>
                            </div>
                        }
                    />
                </p>
                <p>{t("da_info_version_label")} {props.applicableModules.daVersion || CQAppConstant.NOT_AVAILABLE}</p>
            </div>
        );
    }

    return (   
            <>
                <CQTabs 
                    variant="vertical"
                    onSelect={(tabIndex: number) => {
                        onActiveTabChange(tabIndex);
                    }}
                    footer={getFooter()}
                >
                    {getPanel()}
                </CQTabs>
            </>
    );
}

export default CQModule;